export interface TokenSplit {
  input?: number;
  output?: number;
}

/** Typed view of the usage / billing / model headers Passio returns on every call. */
export interface PassioResponseMeta {
  requestId?: string;
  /** Tokens charged for this request (`X-Request-Usage`). */
  requestUsage?: number;
  periodUsage?: number;
  budgetCap?: number;
  modelRequested?: string;
  modelTag?: string;
  billedTokens: TokenSplit & { total?: number };
  modelTokens: TokenSplit;
  priceConvertedTokens: TokenSplit;
  multipliers: TokenSplit;
  wholesaleCostUsd?: number;
  estimatedCostUsd?: number;
  /** Every response header, lower-cased. */
  raw: Record<string, string>;
}

export interface ResponseHeaderDoc {
  name: string;
  description: string;
}

export const RESPONSE_HEADER_DOCS: ResponseHeaderDoc[] = [
  { name: "X-Request-ID", description: "Unique id for this call. Use it with /napi/feedback." },
  { name: "X-Request-Usage", description: "Tokens charged for this request." },
  { name: "X-Period-Usage", description: "Tokens used in the current billing period." },
  { name: "X-Budget-Cap", description: "Token budget for the billing period." },
  { name: "X-Model-Requested", description: "Model requested for AI routes." },
  { name: "X-Model-Tag", description: "Model actually used." },
  { name: "X-Billed-Tokens-Total", description: "Total billed tokens." },
  { name: "X-Billed-Tokens-Input", description: "Billed input tokens." },
  { name: "X-Billed-Tokens-Output", description: "Billed output tokens." },
  { name: "X-Model-Input-Tokens", description: "Raw model input tokens." },
  { name: "X-Model-Output-Tokens", description: "Raw model output tokens." },
  { name: "X-Price-Converted-Input-Tokens", description: "Input tokens after price conversion." },
  { name: "X-Price-Converted-Output-Tokens", description: "Output tokens after price conversion." },
  { name: "X-Input-Multiplier", description: "Price multiplier applied to input tokens." },
  { name: "X-Output-Multiplier", description: "Price multiplier applied to output tokens." },
  { name: "X-Wholesale-Cost-Usd", description: "Wholesale model cost in USD." },
  { name: "X-Estimated-Cost-Usd", description: "Estimated cost in USD." },
];

type HeaderSource = Headers | Record<string, string | string[] | undefined>;

function toRecord(headers: HeaderSource): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof (headers as Headers).forEach === "function" && typeof (headers as Headers).get === "function") {
    (headers as Headers).forEach((v, k) => (out[k.toLowerCase()] = v));
  } else {
    for (const [k, v] of Object.entries(headers as Record<string, string | string[] | undefined>)) {
      if (v !== undefined) out[k.toLowerCase()] = Array.isArray(v) ? v.join(", ") : String(v);
    }
  }
  return out;
}

const num = (v: string | undefined): number | undefined => {
  if (v === undefined || v.trim() === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

export function parseResponseMeta(headers: HeaderSource): PassioResponseMeta {
  const raw = toRecord(headers);
  const h = (name: string) => raw[name.toLowerCase()];
  return {
    requestId: h("X-Request-ID"),
    requestUsage: num(h("X-Request-Usage")),
    periodUsage: num(h("X-Period-Usage")),
    budgetCap: num(h("X-Budget-Cap")),
    modelRequested: h("X-Model-Requested"),
    modelTag: h("X-Model-Tag"),
    billedTokens: {
      total: num(h("X-Billed-Tokens-Total")),
      input: num(h("X-Billed-Tokens-Input")),
      output: num(h("X-Billed-Tokens-Output")),
    },
    modelTokens: { input: num(h("X-Model-Input-Tokens")), output: num(h("X-Model-Output-Tokens")) },
    priceConvertedTokens: {
      input: num(h("X-Price-Converted-Input-Tokens")),
      output: num(h("X-Price-Converted-Output-Tokens")),
    },
    multipliers: { input: num(h("X-Input-Multiplier")), output: num(h("X-Output-Multiplier")) },
    wholesaleCostUsd: num(h("X-Wholesale-Cost-Usd")),
    estimatedCostUsd: num(h("X-Estimated-Cost-Usd")),
    raw,
  };
}

/** Tokens to count against limits: `X-Request-Usage`, falling back to billed total. */
export function tokensCharged(meta: PassioResponseMeta): number {
  return meta.requestUsage ?? meta.billedTokens.total ?? 0;
}
